/**
 * Shopify Checkout URL Generation Utility
 * 
 * Uses the /cart/clear?return_to=/cart/add?...&return_to=/checkout pattern
 * to ensure a clean cart on every checkout attempt.
 * 
 * Flow:
 *   1. /cart/clear — clears any existing cart items
 *   2. Redirects to /cart/add?items[][id]=...&items[][quantity]=... — adds all items
 *   3. Redirects to /checkout — proceeds to Shopify checkout
 * 
 * IMPORTANT: The return_to parameter must be URL-encoded so Shopify parses
 * the nested query string correctly. Without encoding, the store theme
 * intercepts the URL and redirects to the homepage.
 * 
 * Store: ryder-id.myshopify.com (custom domain: ryder.id)
 * 
 * Supports multiple product variants for A/B testing:
 *   Scenario A (default /): variant 51751218774319 — $249 listed, $199 with 2R11SK07QHTW code
 *   Scenario B (/b):        variant 54680271225135 — $179 listed, $145 with Q5R7RB4R14BD code
 */

const STORE_URL = 'https://ryder.id';

// Primary product variants per scenario
export const RYDER_ONE_VARIANTS: Record<string, string> = {
  a: '51751218774319',  // Scenario A — $249 product
  b: '54680271225135',  // Scenario B — $179 product
};

/** @deprecated Use RYDER_ONE_VARIANTS instead */
export const RYDER_ONE_VARIANT = RYDER_ONE_VARIANTS.a;

// Add-ons
export const RECOVERY_TAG_SINGLE_VARIANT = '53113436143919'; // Single tag - FREE with order
export const RECOVERY_TAG_2PACK_VARIANT = '50292858618159';  // 2-Pack upsell - $29
export const WIRELESS_CHARGER_VARIANT = '51993424003375';    // FREE (was $29)

// Tracking parameters
export const TRACKING_PARAMS = {
  referrer: 'skyvane',
} as const;

export type PillowSize = 'Standard' | 'King'; // Kept for compatibility
export type UpsellType = 'recovery-tag' | 'recovery-tag-2pack' | 'recovery-tag-2pack-extra' | null;

export interface CheckoutConfig {
  size: PillowSize;
  quantity: 1 | 2 | 4;
  upsell?: UpsellType;
  secondUpsell?: boolean;
  shippingInsurance?: boolean;
  /** Scenario ID ('a' or 'b') — determines which Shopify variant to use */
  scenarioId?: string;
  /** Optional discount code to apply at checkout */
  discountCode?: string;
}

interface CartItem {
  id: string;
  quantity: number;
}

/**
 * Build the list of cart items based on the checkout config.
 */
function buildCartItems(config: CheckoutConfig): CartItem[] {
  const { upsell, secondUpsell = false, scenarioId = 'a' } = config;

  // Pick the correct Ryder One variant for this scenario
  const ryderOneVariant = RYDER_ONE_VARIANTS[scenarioId] || RYDER_ONE_VARIANTS.a;

  const items: CartItem[] = [];

  // Always add Ryder One (main product)
  items.push({ id: ryderOneVariant, quantity: 1 });

  // Always add FREE single Recovery Tag (included in welcome kit)
  items.push({ id: RECOVERY_TAG_SINGLE_VARIANT, quantity: 1 });

  // Always add Wireless Charger (FREE, part of welcome kit)
  items.push({ id: WIRELESS_CHARGER_VARIANT, quantity: 1 });

  // Add first Recovery Tag 2-Pack if upsell selected ($29)
  if (upsell === 'recovery-tag' || upsell === 'recovery-tag-2pack' || upsell) {
    items.push({ id: RECOVERY_TAG_2PACK_VARIANT, quantity: 1 });
  }

  // Add second Recovery Tag 2-Pack if second upsell selected ($29)
  if (secondUpsell) {
    items.push({ id: RECOVERY_TAG_2PACK_VARIANT, quantity: 1 });
  }

  return items;
}

/**
 * Generate a Shopify checkout URL using the cart/clear → cart/add → checkout pattern.
 * 
 * Format: /cart/clear?return_to={encoded: /cart/add?items[][id]=X&items[][quantity]=Y&return_to=/checkout}
 * 
 * The return_to value is URL-encoded so Shopify correctly parses the nested params.
 * Discount codes are applied via /checkout?discount=CODE in the final redirect.
 */
export function generateCheckoutUrl(config: CheckoutConfig): string {
  const { discountCode } = config;
  const items = buildCartItems(config);

  // Build the /cart/add query string with items
  const addParams: string[] = [];
  for (const item of items) {
    addParams.push(`items[][id]=${item.id}`);
    addParams.push(`items[][quantity]=${item.quantity}`);
  }

  // Build the final checkout redirect path
  let checkoutPath = '/checkout';
  if (discountCode) {
    checkoutPath += `?discount=${encodeURIComponent(discountCode)}`;
  }

  // Build the /cart/add path with return_to=/checkout
  const cartAddPath = `/cart/add?${addParams.join('&')}&return_to=${checkoutPath}`;

  // URL-encode the entire return_to value for the /cart/clear redirect
  const encodedReturnTo = encodeURIComponent(cartAddPath);

  return `${STORE_URL}/cart/clear?return_to=${encodedReturnTo}`;
}
